import { useEffect, useState } from "react";
import {
  Alert, AlertIcon, Box, Button, Flex, FormControl, FormLabel, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter,
  ModalHeader, ModalOverlay, Select, SimpleGrid, Spinner, Tbody, Td, Text, Textarea, Th, Thead, Tr, useToast,
} from "@chakra-ui/react";
import { createShipment, FiscalIcmsProfile, FiscalParty, getShipmentPreview, ShipmentPreview } from "../../../services/fiscalService";
import { crm } from "../../../theme";
import { brl } from "./duimp";
import { formatNcm, TableCard, tdProps, thProps } from "./tableStyles";
import { decimalInput, parseWeight, statusInfo } from "./fiscalLabels";
import FiscalPartyFields, { emptyParty } from "./FiscalPartyFields";

const Label = ({ children }: { children: React.ReactNode }) => (
  <FormLabel fontSize="12px" fontWeight="600" color={crm.textMuted} mb="4px">{children}</FormLabel>
);

const r2 = (v: number) => Math.round(v * 100) / 100;

/** Espelho de OnBehalfShipmentBuilder (só para a prévia; o valor que vale é o do servidor). */
const estimate = (cost: number, ipiRate: number, p?: FiscalIcmsProfile) => {
  const taxed = !!p && !["40", "41", "50"].includes(p.cst);
  const eff = taxed ? (p!.rate / 100) * (1 - p!.baseReduction / 100) : 0;
  const value = eff >= 1 ? cost : r2(cost / (1 - eff));
  const base = taxed ? r2(value * (1 - p!.baseReduction / 100)) : 0;
  return { value, icms: taxed ? r2((base * p!.rate) / 100) : 0, ipi: r2((value * ipiRate) / 100) };
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="13px" fontWeight="700" mt="18px" mb="8px">{children}</Text>
);

/**
 * Gerar remessa por conta e ordem (NF mãe) a partir da NF de entrada autorizada: destinatário (adquirente),
 * transportadora, volumes e perfil de ICMS por item. Gera rascunho; a emissão é na tela da nota.
 */
const ShipmentModal = ({ entryId, onClose, onCreated, onOpenInvoice }: {
  entryId: string;
  onClose: () => void;
  onCreated: (invoiceId: string) => void;
  onOpenInvoice: (invoiceId: string) => void;
}) => {
  const toast = useToast();
  const [preview, setPreview] = useState<ShipmentPreview | null>(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recipient, setRecipient] = useState<FiscalParty>(emptyParty());
  const [withCarrier, setWithCarrier] = useState(true);
  const [carrier, setCarrier] = useState<FiscalParty>(emptyParty());
  const [profile, setProfile] = useState("");
  const [itemProfiles, setItemProfiles] = useState<Record<number, string>>({});
  const [form, setForm] = useState({ freightMode: 1, volumeQuantity: "", volumeKind: "", netWeight: "", grossWeight: "", pisCofinsCst: "" });
  const [fiscoText, setFiscoText] = useState("");
  const [fiscoEdited, setFiscoEdited] = useState(false);

  useEffect(() => {
    getShipmentPreview(entryId)
      .then((p) => {
        setPreview(p);
        if (p.recipient) setRecipient(p.recipient);
        setProfile(p.defaults.defaultProfile);
        setForm({
          freightMode: p.defaults.freightMode,
          volumeQuantity: p.volumeQuantity?.toString() ?? "",
          volumeKind: p.volumeKind ?? "",
          netWeight: decimalInput(p.netWeight),
          grossWeight: decimalInput(p.grossWeight),
          pisCofinsCst: p.defaults.pisCofinsCst,
        });
        setFiscoText(p.fiscoText ?? "");
      })
      .catch((e) => {
        setFailed(true);
        toast({
          status: "error",
          title: e?.response?.status === 403 ? "Sem permissão para emitir notas fiscais" : "Não foi possível carregar a NF de entrada",
        });
      });
  }, [entryId]);

  const set = (field: keyof typeof form, value: any) => setForm((f) => ({ ...f, [field]: value }));
  const profiles = preview?.defaults.icmsProfiles ?? [];
  const profileOf = (itemNumber: number) => profiles.find((p) => p.name === (itemProfiles[itemNumber] || profile));
  const rows = (preview?.items ?? []).map((it) => ({ it, ...estimate(it.cost, it.ipiRate, profileOf(it.itemNumber)) }));
  const total = rows.reduce((s, r) => s + r.value + r.ipi, 0);
  const weights = [form.netWeight, form.grossWeight].map(parseWeight);
  const badNumber = weights.some((w) => w !== null && (Number.isNaN(w) || w < 0));
  const carrierWithoutName = withCarrier && carrier.document.replace(/\D/g, "") !== "" && !carrier.name.trim();
  const blocked = !preview || preview.items.length === 0 || !profile || !preview.entryAuthorized || badNumber || carrierWithoutName;

  const generate = async () => {
    setSaving(true);
    const result = await createShipment(entryId, {
      recipient,
      carrier: withCarrier && carrier.name.trim() ? carrier : null,
      freightMode: form.freightMode,
      volumeQuantity: form.volumeQuantity ? Number(form.volumeQuantity) : null,
      volumeKind: form.volumeKind || null,
      netWeight: weights[0],
      grossWeight: weights[1],
      pisCofinsCst: form.pisCofinsCst || null,
      profile,
      itemProfiles: Object.entries(itemProfiles).filter(([, p]) => p).map(([n, p]) => ({ itemNumber: Number(n), profile: p })),
      fiscoText: fiscoEdited ? fiscoText : null,
    });
    setSaving(false);
    if (!result.success || !result.invoice) {
      toast({ status: "error", title: "Não foi possível gerar a remessa", description: result.errors.join("\n"), duration: 10000, isClosable: true });
      return;
    }
    if (result.warnings.length)
      toast({ status: "warning", title: "Rascunho da remessa gerado com avisos", description: result.warnings.join("\n"), duration: 10000, isClosable: true });
    else toast({ status: "success", title: "Rascunho da remessa gerado" });
    onCreated(result.invoice.id);
  };

  return (
    <Modal isOpen onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="16px">
        <ModalHeader fontSize="17px">
          Gerar remessa por conta e ordem{preview?.entryNumber ? ` · entrada nº ${preview.entryNumber}` : ""}{preview?.processRef ? ` · ${preview.processRef}` : ""}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {failed ? (
            <Text fontSize="13px" color={crm.textMuted} py="24px" textAlign="center">Não foi possível carregar a NF de entrada. Feche e tente de novo.</Text>
          ) : !preview ? (
            <Flex justify="center" py="32px"><Spinner /></Flex>
          ) : (
            <Box>
              {preview.warnings.map((w, i) => (
                <Alert key={i} status="warning" borderRadius="10px" mb="8px" fontSize="13px"><AlertIcon />{w}</Alert>
              ))}
              {preview.existing.length > 0 && (
                <Alert status="info" borderRadius="10px" mb="12px" fontSize="13px">
                  <AlertIcon />
                  <Box>
                    Esta entrada já tem remessa:{" "}
                    {preview.existing.map((nf) => (
                      <Button key={nf.id} variant="link" size="sm" mr="8px" onClick={() => onOpenInvoice(nf.id)}>
                        {nf.number ? `Nº ${nf.number}` : "rascunho"} ({statusInfo[nf.status]?.label ?? nf.status})
                      </Button>
                    ))}
                  </Box>
                </Alert>
              )}

              <SectionTitle>Destinatário (adquirente)</SectionTitle>
              <FiscalPartyFields value={recipient} onChange={setRecipient} />

              <Flex align="center" justify="space-between" mt="18px" mb="8px">
                <Text fontSize="13px" fontWeight="700">Transporte</Text>
                <Button variant="link" size="sm" onClick={() => setWithCarrier((v) => !v)}>
                  {withCarrier ? "Sem transportadora" : "Informar transportadora"}
                </Button>
              </Flex>
              {withCarrier && <FiscalPartyFields carrier value={carrier} onChange={setCarrier} />}
              {carrierWithoutName && <Text mt="6px" fontSize="13px" color="red.500">Informe a razão social da transportadora.</Text>}
              <SimpleGrid columns={{ base: 2, md: 5 }} spacing="10px" mt="10px">
                <FormControl>
                  <Label>Frete</Label>
                  <Select size="sm" value={form.freightMode} onChange={(e) => set("freightMode", Number(e.target.value))}>
                    <option value={0}>0 · remetente</option>
                    <option value={1}>1 · destinatário</option>
                    <option value={2}>2 · terceiros</option>
                    <option value={9}>9 · sem frete</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <Label>Volumes</Label>
                  <Input size="sm" inputMode="numeric" value={form.volumeQuantity} onChange={(e) => set("volumeQuantity", e.target.value.replace(/\D/g, ""))} />
                </FormControl>
                <FormControl>
                  <Label>Espécie</Label>
                  <Input size="sm" maxLength={60} value={form.volumeKind} onChange={(e) => set("volumeKind", e.target.value)} />
                </FormControl>
                <FormControl isInvalid={weights[0] !== null && (Number.isNaN(weights[0]) || weights[0] < 0)}>
                  <Label>Peso líquido (kg)</Label>
                  <Input size="sm" inputMode="decimal" value={form.netWeight} onChange={(e) => set("netWeight", e.target.value)} />
                </FormControl>
                <FormControl isInvalid={weights[1] !== null && (Number.isNaN(weights[1]) || weights[1] < 0)}>
                  <Label>Peso bruto (kg)</Label>
                  <Input size="sm" inputMode="decimal" value={form.grossWeight} onChange={(e) => set("grossWeight", e.target.value)} />
                </FormControl>
              </SimpleGrid>
              {badNumber && <Text mt="6px" fontSize="13px" color="red.500">Peso inválido: use vírgula para decimais (ex.: 1.250,5 ou 1,25).</Text>}

              <SectionTitle>Tributos</SectionTitle>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing="10px" mb="10px">
                <FormControl>
                  <Label>Perfil de ICMS da nota</Label>
                  <Select size="sm" value={profile} onChange={(e) => setProfile(e.target.value)}>
                    {profiles.map((p) => <option key={p.name} value={p.name}>{p.name} · CST {p.cst}{p.benefitCode ? ` · ${p.benefitCode}` : ""}</option>)}
                  </Select>
                </FormControl>
                <FormControl>
                  <Label>CST PIS/COFINS</Label>
                  <Input size="sm" maxLength={3} value={form.pisCofinsCst} onChange={(e) => set("pisCofinsCst", e.target.value.replace(/\D/g, ""))} />
                </FormControl>
              </SimpleGrid>
              <TableCard>
                <Thead>
                  <Tr>
                    <Th {...thProps}>#</Th>
                    <Th {...thProps}>Descrição</Th>
                    <Th {...thProps}>NCM</Th>
                    <Th {...thProps} isNumeric>Qtd.</Th>
                    <Th {...thProps} isNumeric>Custo da entrada</Th>
                    <Th {...thProps}>Perfil de ICMS</Th>
                    <Th {...thProps} isNumeric>Valor (prévia)</Th>
                    <Th {...thProps} isNumeric>ICMS</Th>
                    <Th {...thProps} isNumeric>IPI</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map(({ it, value, icms, ipi }) => (
                    <Tr key={it.itemNumber}>
                      <Td {...tdProps}>{it.itemNumber}</Td>
                      <Td {...tdProps} maxW="260px"><Text noOfLines={2}>{it.description}</Text></Td>
                      <Td {...tdProps} whiteSpace="nowrap">{formatNcm(it.ncm)}</Td>
                      <Td {...tdProps} isNumeric>{it.quantity.toLocaleString("pt-BR")} {it.unit}</Td>
                      <Td {...tdProps} isNumeric>{brl(it.cost)}</Td>
                      <Td {...tdProps} minW="170px">
                        <Select size="xs" value={itemProfiles[it.itemNumber] ?? ""}
                          onChange={(e) => setItemProfiles((m) => ({ ...m, [it.itemNumber]: e.target.value }))}>
                          <option value="">Padrão da nota</option>
                          {profiles.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
                        </Select>
                      </Td>
                      <Td {...tdProps} isNumeric>{brl(value)}</Td>
                      <Td {...tdProps} isNumeric>{brl(icms)}</Td>
                      <Td {...tdProps} isNumeric>{brl(ipi)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </TableCard>
              <Text mt="8px" fontSize="13px" textAlign="right" color={crm.textMuted}>
                Total da nota (produtos + IPI): <b>{brl(total)}</b> · valor = custo da entrada com o ICMS por dentro
              </Text>

              <FormControl mt="14px">
                <Label>Informações ao fisco</Label>
                <Textarea size="sm" rows={3} maxLength={2000} value={fiscoText} onChange={(e) => { setFiscoText(e.target.value); setFiscoEdited(true); }} />
              </FormControl>
            </Box>
          )}
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isLoading={saving} isDisabled={blocked} onClick={generate}>
            Gerar rascunho
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ShipmentModal;
